/**
 * Selection Manager - Handles product selection state and cart operations
 * Part of the SOLID Course Materials System
 *
 * @author Course Materials Team
 * @version 1.0.0
 */

class SelectionManager {
  /**
   * @param {Object} config - Configuration object
   * @param {Array<any>} config.products - Products array
   * @param {any} config.productHandler - SOLID product handler instance
   */
  constructor(config = {}) {
    this.products = config.products || [];
    this.productHandler = config.productHandler || null;
    this.selectedProducts = [];
    this._isSyncing = false; // Flag to prevent re-entrant sync calls

    // Bind methods to preserve context
    this.updateSelectedProducts = this.updateSelectedProducts.bind(this);
    this.updateSelectionBarSummary = this.updateSelectionBarSummary.bind(this);
    this.clearSelections = this.clearSelections.bind(this);
    this.rehydrateCheckboxSelections = this.rehydrateCheckboxSelections.bind(this);
    this._rehydrateChoicePurchaseCheckboxes =
      this._rehydrateChoicePurchaseCheckboxes.bind(this);
    this._syncChoiceProgressAfterRehydrate =
      this._syncChoiceProgressAfterRehydrate.bind(this);
    this.syncChoiceProductFromCheckbox =
      this.syncChoiceProductFromCheckbox.bind(this);
    this.getSelectedCount = this.getSelectedCount.bind(this);
    this.getTotalPrice = this.getTotalPrice.bind(this);
    this.findVariant = this.findVariant.bind(this);
    this.addSelectedProductsToCart = this.addSelectedProductsToCart.bind(this);
  }

  /**
   * Updates the internal selected products array based on checked checkboxes
   * Preserves React (suggested/bundle) items and orphaned checkbox selections whose
   * section is no longer on the page (e.g. user cleared a CMRP section while ATB is populated).
   * @returns {void}
   */
  updateSelectedProducts() {
    const allCheckboxes = Array.from(
      document.querySelectorAll('.purchase-checkbox')
    );
    const liveSectionIds = new Set(
      allCheckboxes
        .map(
          /** @param {any} cb */ cb =>
            this._resolveSelectionScope(cb).sectionId ||
            String(cb.dataset.sectionId || '').trim()
        )
        .filter(Boolean)
    );

    // Preserve React selections, items from a removed course card, and live
    // remaining-course rows whose checkboxes have not been re-checked yet
    // after a re-render. Only drop a live row when its matching checkbox
    // exists and is unchecked (intentional deselect).
    const preservedItems = this.selectedProducts.filter(item => {
      if (item.source === 'suggested' || item.source === 'bundle') return true;
      const sid = String(item.sectionId || '').trim();
      if (sid && !liveSectionIds.has(sid)) return true;
      const match = allCheckboxes.find(/** @param {any} cb */ cb =>
        this._checkboxMatchesSnapshotItem(cb, item)
      );
      if (!match) return true;
      return Boolean(match.checked);
    });
    this.selectedProducts = [...preservedItems];
    this._dedupeSelectedProducts();
    const checkboxes = document.querySelectorAll('.purchase-checkbox:checked');
    checkboxes.forEach(
      /** @param {any} checkbox */ checkbox => {
        // Choice cards persist via selectedSuggestedMaterials (same as Suggested).
        if (checkbox.closest('[id^="choice-details-product-"]')) {
          return;
        }
        const materialItem = checkbox.closest('.material-item');
        // Suggested/bundle/choice drawers use .purchase-checkbox outside a card.
        if (!materialItem) return;
        const selectedFormat =
          this.getSelectedFormat(materialItem) ||
          (checkbox.dataset.format
            ? String(checkbox.dataset.format).toLowerCase()
            : null);
        const cardProductId = materialItem.querySelector(
          '.choice-product-card__plus-select[data-product-id]'
        )?.getAttribute('data-product-id');
        const productId = cardProductId || checkbox.dataset.productId;
        const purchaseType = checkbox.dataset.purchaseType;
        const variantId = checkbox.dataset.variantId;
        const productTitle = checkbox.dataset.productTitle;
        const { sectionId, instanceId } = this._resolveSelectionScope(checkbox);
        // Check if this is a simple product (determined by html-renderer based on variant data)
        const formatBtns = materialItem.querySelectorAll('.format-btn');
        const formatTabs = materialItem.querySelectorAll('.format-tab');
        const isSimple = formatBtns.length === 0 && formatTabs.length === 0;

        // Simple products only need productId & purchaseType, complex products also need selectedFormat
        const hasRequiredData = isSimple
          ? productId && purchaseType
          : selectedFormat && productId && purchaseType;

        if (hasRequiredData) {
          let product = window.courseData.products_Materials.find(
            /** @param {any} p */ p => p.id == productId
          );

          // For simple products, use variant ID directly. For complex products, use format-based lookup
          let variant = isSimple
            ? product?.variants.find(
                /** @param {any} v */ v => v.id == variantId
              )
            : product?.variants.find(
                /** @param {any} v */ v => v.id == variantId
              ) || this.findVariant(productId, selectedFormat, purchaseType);

          if (!product && variantId) {
            product = window.courseData.products_Materials.find(
              /** @param {any} p */ p =>
                (p.variants || []).some(
                  /** @param {any} v */ v => v.id == variantId
                )
            );
            if (product) {
              variant = product.variants.find(
                /** @param {any} v */ v => v.id == variantId
              );
            }
          }

          if (variant) {
            // Deduplicate by (variantId, sectionId) - same product in different sections adds twice
            const extractedVariantId = this.extractVariantId(variant.id);
            const alreadyExists = this.selectedProducts.some(item => {
              const sameVariant = this.extractVariantId(item.variant?.id) === extractedVariantId;
              if (item.source === 'suggested' || item.source === 'bundle') {
                const itemKey = this._getSelectionCompositeKey(
                  item.productId,
                  item.variant?.id,
                  item.sectionId || item.instanceId
                );
                const checkboxKey = this._getSelectionCompositeKey(
                  productId,
                  variant.id,
                  sectionId || instanceId
                );
                return itemKey === checkboxKey;
              }
              return sameVariant &&
                (item.sectionId || '') === sectionId &&
                (!instanceId || !item.instanceId || (item.instanceId || '') === instanceId);
            });

            if (!alreadyExists) {
              const courseCard = checkbox.closest('.course-card');
              const cartProps = this._collectCartLineProps({
                checkbox,
                product,
                variant,
                sectionId,
                courseCard,
              });
              this.selectedProducts.push({
                productId,
                productTitle,
                selectedFormat: selectedFormat || 'simple',
                purchaseType,
                variant,
                price: parseFloat(variant.price) || 0,
                sectionId,
                instanceId,
                ...cartProps,
              });
            }
          }
        }
      }
    );

    // Dispatch custom event for external listeners
    this.dispatchSelectionChangeEvent();
  }

  /**
   * Updates the selection bar component with current totals
   * @returns {void}
   */
  updateSelectionBarSummary() {
    const selectionBar = document.querySelector('selection-bar-component');
    if (!selectionBar) return;

    const totalItems = this.selectedProducts.length;
    const totalPrice = this.selectedProducts.reduce(
      (sum, /** @param {any} item */ item) => sum + item.price,
      0
    );

    // Only update if values actually changed to prevent unnecessary updates
    const currentCount = selectionBar.currentSelectedCount;
    const currentTotal = parseFloat(selectionBar.currentSelectedTotal || '0');
    const newTotal = parseFloat(totalPrice.toFixed(2));

    if (currentCount !== totalItems || currentTotal !== newTotal) {
      // Update selection bar display
      selectionBar.currentSelectedCount = totalItems;
      selectionBar.currentSelectedTotal = totalPrice.toFixed(2);
      selectionBar.updateVisibility();
    }

    if (typeof selectionBar.updateRequiredProgress === 'function') {
      selectionBar.updateRequiredProgress();
    }
  }

  /**
   * Gets the selected format from a material item
   * @param {Element} materialItem - The material item DOM element
   * @returns {string|null} The selected format or null
   */
  getSelectedFormat(materialItem) {
    if (!materialItem) return null;

    const selectedBtn = materialItem.querySelector('.format-btn.selected');
    if (selectedBtn) {
      const fmt = selectedBtn.getAttribute('data-format');
      if (fmt) return String(fmt).toLowerCase();
    }

    // CMRP cards use format tabs / collapses instead of .format-btn
    const activeTab = materialItem.querySelector(
      '.format-tab.active, .format-collapse.active'
    );
    if (activeTab) {
      const fmt = activeTab.getAttribute('data-format');
      if (fmt) return String(fmt).toLowerCase();
    }

    const checked = materialItem.querySelector('.purchase-checkbox:checked');
    if (checked?.dataset?.format) {
      return String(checked.dataset.format).toLowerCase();
    }

    return null;
  }

  /**
   * Finds a variant by product ID, format, and purchase type
   * @param {string} productId - Product ID
   * @param {string} selectedFormat - Selected format (digital/print)
   * @param {string} purchaseType - Purchase type (buy/rent/etc)
   * @returns {any|null} The variant or null if not found
   */
  findVariant(productId, selectedFormat, purchaseType) {
    if (!this.productHandler) return null;
    return this.productHandler.findVariant(
      productId,
      selectedFormat,
      purchaseType
    );
  }

  /**
   * Clears all selected products
   * @returns {void}
   */
  clearSelections() {
    this.selectedProducts = [];

    // Uncheck all checkboxes. Setting .checked does not fire `change`, so
    // purchase-type .selected / choice chrome must be synced explicitly.
    document
      .querySelectorAll(
        '.purchase-checkbox:checked, .purchase-choice-checkbox:checked'
      )
      .forEach(/** @param {any} checkbox */ checkbox => {
        checkbox.checked = false;
      });

    // Also clear React component selections (suggested + bundle)
    this._clearReactSelectionStore('selectedSuggestedMaterials', 'selectedSuggestedMaterialsListeners');
    this._clearReactSelectionStore('selectedBundleMaterials', 'selectedBundleMaterialsListeners');

    if (typeof window.CmrpSessionSnapshot?.saveSelections === 'function') {
      window.CmrpSessionSnapshot.saveSelections([], { replace: true });
    }

    this.updateSelectionBarSummary();
    this.dispatchSelectionChangeEvent();
    this._syncDomAfterSelectionClear();
  }

  /**
   * Visual bits that normally update on checkbox `change` — selected variant
   * highlight, fee strips, choice plus/summary, multi-format warnings.
   * @private
   * @returns {void}
   */
  _syncDomAfterSelectionClear() {
    if (typeof window.cmrpSyncPurchaseTypeSelectedClass === 'function') {
      window.cmrpSyncPurchaseTypeSelectedClass(document);
    }

    if (typeof window.syncAllCmrpVariantNotices === 'function') {
      window.syncAllCmrpVariantNotices();
    } else if (typeof window.syncCmrpVariantNoticeInCollapse === 'function') {
      document.querySelectorAll('.collapse-content').forEach(collapse => {
        window.syncCmrpVariantNoticeInCollapse(collapse);
      });
    }

    const mim = window.courseMaterialsPage?.components?.interactionManager;
    if (!mim) return;

    document.querySelectorAll('.choice-item').forEach(item => {
      if (typeof mim._syncChoiceItemChrome === 'function') {
        mim._syncChoiceItemChrome(item);
      }
    });
    document.querySelectorAll('.material-item').forEach(item => {
      if (typeof mim._updateMultiFormatWarning === 'function') {
        mim._updateMultiFormatWarning(item);
      }
    });
    this._syncChoiceProgressAfterRehydrate();
  }

  /**
   * Re-check regular product card checkboxes from retained SelectionManager state
   * after CMRP sections re-render (Suggested/bundle already remount from window stores).
   * @returns {void}
   */
  rehydrateCheckboxSelections() {
    const retained = this.selectedProducts.filter(
      item => item.source !== 'suggested' && item.source !== 'bundle'
    );

    const checkboxes = Array.from(
      document.querySelectorAll('.purchase-checkbox')
    );

    /** @type {Set<Element>} */
    const touchedCards = new Set();

    if (retained.length && checkboxes.length) {
      retained.forEach(item => {
        if (!this.extractVariantId(item.variant?.id)) return;

        const checkbox = checkboxes.find(/** @param {any} cb */ cb => {
          return this._checkboxMatchesSnapshotItem(cb, item);
        });

        if (!checkbox || checkbox.disabled) return;

        checkbox.checked = true;

        const materialItem = checkbox.closest('.material-item');
        if (!materialItem) return;

        touchedCards.add(materialItem);

        const selectedFormat = String(
          item.selectedFormat || checkbox.dataset.format || ''
        ).toLowerCase();
        const formatBtns = materialItem.querySelectorAll('.format-btn');
        if (formatBtns.length && selectedFormat && selectedFormat !== 'simple') {
          formatBtns.forEach(/** @param {Element} btn */ btn => {
            const fmt = String(btn.getAttribute('data-format') || '').toLowerCase();
            btn.classList.toggle('selected', fmt === selectedFormat);
          });
        }

        // Show the format tab/collapse that owns this checkbox (CMRP cards use tabs, not format-btn)
        const collapse = checkbox.closest('.format-collapse');
        const container =
          collapse?.closest('.purchase-options-container') ||
          materialItem.querySelector('.purchase-options-container');
        const format =
          collapse?.getAttribute('data-format') ||
          checkbox.dataset.format ||
          (selectedFormat !== 'simple' ? selectedFormat : '');
        if (container && format) {
          const formatKey = String(format).toLowerCase();
          container.querySelectorAll('.format-tab').forEach(/** @param {Element} tab */ tab => {
            const active =
              String(tab.getAttribute('data-format') || '').toLowerCase() ===
              formatKey;
            tab.classList.toggle('active', active);
          });
          container
            .querySelectorAll('.format-collapse')
            .forEach(/** @param {HTMLElement} c */ c => {
              const active =
                String(c.getAttribute('data-format') || '').toLowerCase() ===
                formatKey;
              c.classList.toggle('active', active);
              c.style.display = active ? 'block' : 'none';
            });
        }
      });

      touchedCards.forEach(materialItem => {
        if (typeof window.cmrpSyncPurchaseTypeSelectedClass === 'function') {
          window.cmrpSyncPurchaseTypeSelectedClass(materialItem);
        }
        materialItem.querySelectorAll('.collapse-content').forEach(collapse => {
          if (typeof window.syncCmrpVariantNoticeInCollapse === 'function') {
            window.syncCmrpVariantNoticeInCollapse(collapse);
          }
        });

        const productId =
          materialItem
            .querySelector('.choice-product-card__plus-select[data-product-id]')
            ?.getAttribute('data-product-id') ||
          materialItem
            .querySelector('.purchase-checkbox:checked')
            ?.getAttribute('data-product-id');
        const mim = window.courseMaterialsPage?.components?.interactionManager;
        if (
          productId &&
          typeof mim?._updateChoiceProductPlusButton === 'function'
        ) {
          mim._updateChoiceProductPlusButton(productId);
        }
        if (typeof mim?._updateMultiFormatWarning === 'function') {
          mim._updateMultiFormatWarning(materialItem);
        }
      });
    }

    this._rehydrateChoicePurchaseCheckboxes(checkboxes);
    this.updateSelectedProducts();
    this._syncChoiceProgressAfterRehydrate();
  }

  /**
   * Choice cards live in vanilla DOM (not the Suggested Preact store). Re-check
   * their purchase-checkboxes from restored regular *and* suggested-store rows,
   * scoped to the course selection the variant was originally picked from.
   * @param {Element[]} checkboxes
   * @returns {void}
   */
  _rehydrateChoicePurchaseCheckboxes(checkboxes) {
    const choiceBoxes = (checkboxes || []).filter(cb =>
      cb.closest('[id^="choice-details-product-"]')
    );
    if (!choiceBoxes.length) return;

    /** @type {Array<object>} */
    const restoredRows = [];
    const pushRow = (item, source) => {
      const variantId = this.extractVariantId(item?.variant?.id ?? item?.id);
      if (!variantId) return;
      restoredRows.push({
        variantId,
        productId: item?.productId || item?.variant?.parentProductId || '',
        sectionId: String(item?.sectionId || item?.instanceId || '').trim(),
        instanceId: String(item?.instanceId || item?.sectionId || '').trim(),
        source,
      });
    };
    (this.selectedProducts || []).forEach(item => {
      if (item.source === 'bundle') return;
      pushRow(item, item.source || '');
    });
    const suggested = window.selectedSuggestedMaterials;
    if (Array.isArray(suggested)) {
      suggested.forEach(item => {
        const normalized = this._normalizeReactSelectionEntry(item);
        pushRow(
          {
            variant: normalized.variant,
            productId: normalized.productId,
            sectionId: normalized.sectionId,
            instanceId: normalized.instanceId,
          },
          'suggested'
        );
      });
    }
    if (!restoredRows.length) {
      return;
    }

    const mim = window.courseMaterialsPage?.components?.interactionManager;
    const touchedPids = new Set();
    choiceBoxes.forEach(cb => {
      if (cb.disabled) return;
      const matches = restoredRows.filter(row =>
        this._checkboxMatchesSnapshotItem(cb, row)
      );
      if (!matches.length) return;
      cb.checked = true;
      const pid = cb.getAttribute('data-product-id');
      if (pid) touchedPids.add(pid);
    });
    touchedPids.forEach(pid => {
      if (typeof mim?._updateChoiceProductPlusButton === 'function') {
        mim._updateChoiceProductPlusButton(pid);
      }
    });
    document.querySelectorAll('.choice-item').forEach(item => {
      if (typeof mim?._syncChoiceItemChrome === 'function') {
        mim._syncChoiceItemChrome(item);
      }
      if (typeof window.cmrpSyncPurchaseTypeSelectedClass === 'function') {
        window.cmrpSyncPurchaseTypeSelectedClass(item);
      }
    });
  }

  /**
   * Refresh Choice progress header / collapsed summary after checkbox restore.
   * @returns {void}
   */
  _syncChoiceProgressAfterRehydrate() {
    const mim = window.courseMaterialsPage?.components?.interactionManager;
    if (typeof mim?._syncChoiceMinimumAndExtraData !== 'function') return;
    const run = () => {
      const sections = Array.from(
        document.querySelectorAll('[id^="choice-minimum-"]')
      ).map(el => el.id.slice('choice-minimum-'.length));
      sections.forEach(sectionID => {
        if (sectionID) mim._syncChoiceMinimumAndExtraData(sectionID);
      });
    };
    run();
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(run);
    }
    setTimeout(run, 0);
  }

  /**
   * Gets the count of selected products
   * @returns {number} Number of selected products
   */
  getSelectedCount() {
    return this.selectedProducts.length;
  }

  /**
   * Gets the total price of selected products
   * @returns {number} Total price
   */
  getTotalPrice() {
    return this.selectedProducts.reduce(
      (sum, /** @param {any} item */ item) => sum + item.price,
      0
    );
  }

  /**
   * Gets the current selected products array
   * @returns {Array<any>} Array of selected products
   */
  getSelectedProducts() {
    return [...this.selectedProducts]; // Return a copy to prevent mutation
  }

  /**
   * Adds selected products to cart
   * @returns {Promise<void>}
   */
  async addSelectedProductsToCart() {
    if (
      this.productHandler &&
      typeof this.productHandler.addSelectedProductsToCart === 'function'
    ) {
      return await this.productHandler.addSelectedProductsToCart();
    }

    // Fallback implementation
    this.fallbackAddToCart();
  }

  /**
   * Fallback implementation for adding to cart
   * @private
   * @returns {void}
   */
  fallbackAddToCart() {
    if (this.selectedProducts.length === 0) {
      alert('Please select some products first.');
      return;
    }


    // You could implement Shopify Cart API calls here
    // For now, just show a success message
    alert(`Added ${this.selectedProducts.length} item(s) to cart!`);

    // Clear selections after adding to cart
    this.clearSelections();
  }

  /**
   * Dispatches a custom event when selections change
   * @private
   * @returns {void}
   */
  dispatchSelectionChangeEvent() {
    const event = new CustomEvent('selection:changed', {
      detail: {
        selectedProducts: this.getSelectedProducts(),
        totalCount: this.getSelectedCount(),
        totalPrice: this.getTotalPrice(),
      },
    });
    document.dispatchEvent(event);
  }

  /**
   * Updates both selected products and selection bar summary
   * Convenience method that combines both operations
   * @returns {void}
   */
  refreshSelections() {
    this.updateSelectedProducts();
    this.syncSuggestedMaterials();
    this.syncBundleMaterials();
    this.updateSelectionBarSummary();
  }

  /**
   * Clear a React selection window store and notify its listeners.
   * @private
   * @param {string} storeName
   * @param {string} listenersName
   * @returns {void}
   */
  _clearReactSelectionStore(storeName, listenersName) {
    if (typeof window === 'undefined' || !window[storeName]) return;
    window[storeName] = [];
    if (window[listenersName]) {
      window[listenersName].forEach(listener => {
        try {
          listener();
        } catch (e) {
          console.error('Error in selection change listener:', e);
        }
      });
    }
  }

  /**
   * Normalize a React selection store entry to { variant, productId, sectionId, instanceId }.
   * @private
   * @param {any} entry
   * @returns {{ variant: any, productId: string|null, sectionId: string, instanceId: string }}
   */
  _normalizeReactSelectionEntry(entry) {
    if (!entry) {
      return { variant: null, productId: null, sectionId: '', instanceId: '' };
    }

    const sectionId = String(entry.sectionId || entry.instanceId || '').trim();
    const instanceId = String(entry.instanceId || entry.sectionId || '').trim();

    if (entry.variant) {
      return {
        variant: entry.variant,
        productId: entry.productId || entry.variant.parentProductId || null,
        sectionId,
        instanceId,
      };
    }

    return {
      variant: entry,
      productId: entry.parentProductId || null,
      sectionId,
      instanceId,
    };
  }

  /**
   * Course section + material-card instance for a checkbox / material node.
   * @private
   * @param {Element|null|undefined} el
   * @returns {{ sectionId: string, instanceId: string }}
   */
  _resolveSelectionScope(el) {
    if (!el) return { sectionId: '', instanceId: '' };
    const portaledRoot = el.closest?.('[id^="choice-details-product-"]');
    const scopeEl = portaledRoot?.__cmrpChoiceOuter || el;
    const materialItem =
      scopeEl.closest?.('.material-item') || el.closest?.('.material-item');
    const instanceId = String(
      materialItem?.dataset?.purchaseInstanceId || ''
    ).trim();
    const fromCard = String(
      scopeEl.closest?.('.course-card')?.dataset?.selectedSectionId ||
        el.closest?.('.course-card')?.dataset?.selectedSectionId ||
        ''
    ).trim();
    const choiceRoot =
      scopeEl.closest?.('.choice-products-section') ||
      el.closest?.('.choice-products-section');
    const minEl = choiceRoot?.querySelector('[id^="choice-minimum-"]');
    const fromChoice = minEl?.id
      ? minEl.id.slice('choice-minimum-'.length)
      : '';
    const fromAttr = String(
      el.dataset?.sectionId || el.getAttribute?.('data-section-id') || ''
    ).trim();
    let sectionId = fromCard || fromChoice || fromAttr;
    if (sectionId && instanceId && sectionId === instanceId) {
      sectionId = fromCard || fromChoice || '';
    }
    return { sectionId, instanceId };
  }

  /**
   * Match a live checkbox to a snapshot / in-memory selection row.
   * Requires the same variant, and the same course selection when known.
   * @private
   * @param {HTMLElement} checkbox
   * @param {object} item
   * @returns {boolean}
   */
  _checkboxMatchesSnapshotItem(checkbox, item) {
    if (!checkbox || !item) return false;
    const targetVariantId = this.extractVariantId(
      item.variantId || item.variant?.id || item.id
    );
    const checkboxVariantId = this.extractVariantId(checkbox.dataset.variantId);
    if (!targetVariantId || checkboxVariantId !== targetVariantId) {
      return false;
    }

    const { sectionId: cbSection, instanceId: cbInstance } =
      this._resolveSelectionScope(checkbox);
    const targetSection = String(item.sectionId || item.instanceId || '').trim();
    const targetInstance = String(item.instanceId || '').trim();

    if (targetSection) {
      if (cbSection && cbSection !== targetSection) return false;
      if (!cbSection && targetInstance && cbInstance && cbInstance !== targetInstance) {
        return false;
      }
      if (!cbSection && !targetInstance) return false;
    } else {
      // Legacy rows with no selection scope: only restore when this variant
      // appears on a single checkbox so we do not stamp other selections.
      const sameVariantBoxes = Array.from(
        document.querySelectorAll('.purchase-checkbox')
      ).filter(
        /** @param {any} cb */ cb =>
          this.extractVariantId(cb.dataset.variantId) === targetVariantId
      );
      if (sameVariantBoxes.length > 1) return false;
    }

    const instanceIsCardScoped =
      Boolean(targetInstance) && targetInstance !== targetSection;
    if (instanceIsCardScoped && cbInstance && cbInstance !== targetInstance) {
      return false;
    }

    return true;
  }

  /**
   * Build a product+variant composite key for deduplication.
   * Includes the course selection (instanceId / sectionId) when present.
   * @private
   * @param {string|null|undefined} productId
   * @param {string|null|undefined} variantId
   * @param {string|null|undefined} instanceId
   * @returns {string}
   */
  _getSelectionCompositeKey(productId, variantId, instanceId = '') {
    const product = this.extractProductId(productId);
    const variant = this.extractVariantId(variantId);
    if (!product || !variant) return '';
    const base = `${product}:${variant}`;
    const inst = instanceId ? String(instanceId).trim() : '';
    return inst ? `${inst}:${base}` : base;
  }

  /**
   * Collapse duplicate queue rows (same variant + source + section + product).
   * Course-remove refresh used to re-hydrate Suggested rows on top of the live queue.
   * @private
   * @returns {void}
   */
  _dedupeSelectedProducts() {
    const seen = new Set();
    this.selectedProducts = this.selectedProducts.filter(item => {
      const variantId = this.extractVariantId(item.variant?.id || item.variantId);
      const source = String(item.source || '').trim();
      const sectionId = String(item.sectionId || item.instanceId || '').trim();
      const productId = String(item.productId || '').trim();
      if (!variantId) return true;
      const key = `${variantId}|${source}|${sectionId}|${productId}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  /**
   * First non-empty trimmed string.
   * @private
   * @param {...unknown} values
   * @returns {string}
   */
  _firstNonEmpty(...values) {
    for (const value of values) {
      if (value == null) continue;
      const s = String(value).trim();
      if (s) return s;
    }
    return '';
  }

  /**
   * @private
   * @param {any} variant
   * @param {(mf: any) => boolean} predicate
   * @param {string} field
   * @returns {string}
   */
  _variantMetafieldValue(variant, predicate, field) {
    const list = Array.isArray(variant?.metafields) ? variant.metafields : [];
    const hit = list.find(predicate);
    if (!hit) return '';
    const value = hit[field];
    return value == null ? '' : String(value).trim();
  }

  /**
   * @private
   * @param {string} sectionId
   * @returns {HTMLElement|null}
   */
  _courseCardForSection(sectionId) {
    const sid = String(sectionId || '').trim();
    if (!sid || typeof document === 'undefined') return null;
    try {
      return document.querySelector(
        `.course-card[data-selected-section-id="${CSS.escape(sid)}"]`
      );
    } catch (e) {
      return document.querySelector(
        `.course-card[data-selected-section-id="${sid}"]`
      );
    }
  }

  /**
   * Course / ISBN / taxonomy / digital flags used as Shopify cart line properties.
   * Same fields for regular DDCS cards and Suggested / Choice / Bundle.
   * @private
   * @param {{ checkbox?: HTMLElement, product?: any, variant?: any, sectionId?: string, courseCard?: Element, existing?: object }} ctx
   * @returns {object}
   */
  _collectCartLineProps(ctx = {}) {
    const checkbox = ctx.checkbox;
    const product = ctx.product;
    const variant = ctx.variant;
    const existing = ctx.existing || {};
    const ds = checkbox?.dataset || {};
    const courseCard =
      ctx.courseCard ||
      checkbox?.closest?.('.course-card') ||
      this._courseCardForSection(ctx.sectionId);
    const card = courseCard?.dataset || {};
    const pick = (...vals) => this._firstNonEmpty(...vals);

    const vid = this.extractVariantId(variant?.id);
    const digitalMap = product?.metafields?.custom?.variant_digital_status;
    const digitalFromProduct =
      vid &&
      digitalMap &&
      (digitalMap[vid] === true || String(digitalMap[vid]).toLowerCase() === 'true')
        ? 'true'
        : '';
    const digitalFromVariant =
      window.ProductSystem?.getVariantIsDigitalString?.(variant, product) === 'true'
        ? 'true'
        : '';

    const publisherFromVariant = this._variantMetafieldValue(
      variant,
      mf => mf && mf.publisher_code && mf.namespace === 'course_materials',
      'publisher_code'
    );

    return {
      campus: pick(existing.campus, ds.campus, card.campus),
      term: pick(existing.term, ds.term, card.term),
      termId: pick(existing.termId, ds.termId, card.termId),
      division: pick(existing.division, ds.division, card.division),
      department: pick(existing.department, ds.department, card.department),
      course: pick(existing.course, ds.course, card.course),
      section: pick(existing.section, ds.section, card.section),
      bookISBN: pick(
        existing.bookISBN,
        ds.bookIsbn,
        this._variantMetafieldValue(variant, mf => mf?.isbn_ean, 'isbn_ean'),
        product?.metafields?.isbn_ean,
        product?.metafields?.course_materials?.isbn_ean
      ),
      bookEdition: pick(
        existing.bookEdition,
        ds.bookEdition,
        this._variantMetafieldValue(variant, mf => mf?.edition, 'edition'),
        product?.metafields?.edition,
        product?.metafields?.course_materials?.edition
      ),
      bookCopyright: pick(
        existing.bookCopyright,
        ds.bookCopyright,
        this._variantMetafieldValue(
          variant,
          mf => mf?.copyright_year,
          'copyright_year'
        ),
        product?.metafields?.copyright_year,
        product?.metafields?.course_materials?.copyright_year
      ),
      bookPublisher: pick(
        existing.bookPublisher,
        ds.bookPublisher,
        this._variantMetafieldValue(
          variant,
          mf => mf?.publisher_name,
          'publisher_name'
        ),
        product?.metafields?.publisher_name,
        product?.metafields?.course_materials?.publisher_name
      ),
      bookRentDueDate: pick(
        existing.bookRentDueDate,
        ds.bookRentDueDate,
        this._variantMetafieldValue(
          variant,
          mf => mf?.expiration_date,
          'expiration_date'
        )
      ),
      format: pick(existing.format, ds.format, variant?.option1),
      type: pick(existing.type, ds.type, variant?.option2),
      departmentData: pick(
        existing.departmentData,
        ds.departmentTaxonomy,
        this._variantMetafieldValue(
          variant,
          mf => mf?.department && mf.namespace === 'taxonomy',
          'department'
        )
      ),
      subDepartment: pick(
        existing.subDepartment,
        ds.subDepartment,
        this._variantMetafieldValue(
          variant,
          mf => mf?.sub_department && mf.namespace === 'taxonomy',
          'sub_department'
        )
      ),
      classds: pick(
        existing.classds,
        ds.class,
        this._variantMetafieldValue(
          variant,
          mf => mf?.class && mf.namespace === 'taxonomy',
          'class'
        )
      ),
      subClass: pick(
        existing.subClass,
        ds.subClass,
        this._variantMetafieldValue(
          variant,
          mf => mf?.sub_class && mf.namespace === 'taxonomy',
          'sub_class'
        )
      ),
      rentalOnlySku: pick(
        existing.rentalOnlySku,
        ds.rentalOnlySku,
        this._variantMetafieldValue(
          variant,
          mf => mf?.rental_only_sku && mf.namespace === 'cm_rental',
          'rental_only_sku'
        )
      ),
      subscriptionLength: pick(
        existing.subscriptionLength,
        ds.subscriptionLength,
        this._variantMetafieldValue(variant, mf => mf?.subscription, 'subscription')
      ),
      isDigital: pick(
        existing.isDigital,
        ds.isDigital,
        digitalFromProduct,
        digitalFromVariant,
        'false'
      ),
      publisherCode: pick(
        existing.publisherCode,
        ds.publisherCode,
        publisherFromVariant,
        product?.metafields?.course_materials?.publisher_code,
        product?.metafields?.publisher_code
      ),
    };
  }

  /**
   * Collect products from course data and bundle component instances.
   * @private
   * @returns {Array<any>}
   */
  _getAllSyncProducts() {
    const allProducts = this.products && this.products.length > 0
      ? [...this.products]
      : [...(window.courseData?.products_Materials || [])];

    if (typeof document !== 'undefined') {
      document.querySelectorAll('bundle-products-component[products-data]').forEach(node => {
        try {
          const bundleProducts = JSON.parse(node.getAttribute('products-data') || '[]');
          if (Array.isArray(bundleProducts)) {
            bundleProducts.forEach(product => {
              const exists = allProducts.some(p => String(p.id) === String(product.id));
              if (!exists) {
                allProducts.push(product);
              }
            });
          }
        } catch (e) {
          // Ignore malformed bundle product payloads
        }
      });

      document.querySelectorAll('suggested-materials-component[products-data]').forEach(node => {
        try {
          const suggestedProducts = JSON.parse(
            node.dataset.cmrpProducts || node.getAttribute('products-data') || '[]'
          );
          if (Array.isArray(suggestedProducts)) {
            suggestedProducts.forEach(product => {
              const exists = allProducts.some(p => String(p.id) === String(product.id));
              if (!exists) {
                allProducts.push(product);
              }
            });
          }
        } catch (e) {
          // Ignore malformed suggested product payloads
        }
      });

      document.querySelectorAll('choice-products-component').forEach(node => {
        try {
          const choiceProducts = JSON.parse(
            node.dataset.cmrpProducts || node.getAttribute('products-data') || '[]'
          );
          if (Array.isArray(choiceProducts)) {
            choiceProducts.forEach(product => {
              const exists = allProducts.some(p => String(p.id) === String(product.id));
              if (!exists) {
                allProducts.push(product);
              }
            });
          }
        } catch (e) {
          // Ignore malformed choice product payloads
        }
      });

      document.querySelectorAll('.choice-item').forEach(item => {
        const product = item.__cmrpProduct;
        if (!product?.id) return;
        const exists = allProducts.some(p => String(p.id) === String(product.id));
        if (!exists) {
          allProducts.push(product);
        }
      });
    }

    return allProducts;
  }

  /**
   * Notify React selection store listeners (Suggested/Choice + Bundle).
   * @private
   * @param {string} listenersName
   * @returns {void}
   */
  _notifyReactSelectionListeners(listenersName) {
    (window[listenersName] || []).forEach(listener => {
      try {
        listener();
      } catch (e) {
        console.error('Error in selection change listener:', e);
      }
    });
  }

  /**
   * Resolve a Choice product from a details panel / checkbox.
   * @private
   * @param {string} productId
   * @returns {object|null}
   */
  _findChoiceProductById(productId) {
    if (!productId) return null;
    const numeric = this.extractProductId(productId);
    const plus =
      document.querySelector(
        `.choice-product-card__plus-select[data-product-id="${productId}"]`
      ) ||
      (numeric
        ? document.querySelector(
            `.choice-product-card__plus-select[data-product-id="${numeric}"]`
          )
        : null);
    const item = plus?.closest('.choice-item');
    if (item?.__cmrpProduct) return item.__cmrpProduct;

    const allProducts = this._getAllSyncProducts();
    return (
      allProducts.find(
        product => this.extractProductId(product?.id) === numeric
      ) || null
    );
  }

  /**
   * Mirror a Choice card's checked variants into selectedSuggestedMaterials
   * so the session snapshot updates the same way Suggested selections do.
   * @param {HTMLInputElement} checkbox
   * @returns {void}
   */
  syncChoiceProductFromCheckbox(checkbox) {
    const panel = checkbox?.closest?.('[id^="choice-details-product-"]');
    if (!panel) return;

    const productId = panel.id.replace(/^choice-details-product-/, '');
    const product = this._findChoiceProductById(productId);
    if (!product) return;

    const pid = this.extractProductId(product.id);
    const { sectionId, instanceId } = this._resolveSelectionScope(checkbox);
    const selectionScope = sectionId || instanceId;
    const checkedVids = new Set(
      Array.from(panel.querySelectorAll('.purchase-checkbox:checked'))
        .map(cb => this.extractVariantId(cb.dataset.variantId))
        .filter(Boolean)
    );

    if (!window.selectedSuggestedMaterials) {
      window.selectedSuggestedMaterials = [];
    }

    window.selectedSuggestedMaterials = window.selectedSuggestedMaterials.filter(
      entry => {
        const normalized = this._normalizeReactSelectionEntry(entry);
        if (this.extractProductId(normalized.productId) !== pid) return true;
        const entryScope = String(
          normalized.sectionId || normalized.instanceId || ''
        ).trim();
        if (selectionScope) {
          return entryScope !== selectionScope;
        }
        return false;
      }
    );

    (product.variants || []).forEach(variant => {
      const vid = this.extractVariantId(variant?.id);
      if (!vid || !checkedVids.has(vid)) return;
      window.selectedSuggestedMaterials.push({
        variant: {
          ...variant,
          parentProductId: variant.parentProductId || product.id,
        },
        productId: product.id,
        sectionId,
        instanceId: instanceId || sectionId,
      });
    });

    this._notifyReactSelectionListeners('selectedSuggestedMaterialsListeners');
  }

  /**
   * Sync React component selections into SelectionManager.
   * @private
   * @param {string} storeName
   * @param {string} source
   * @returns {void}
   */
  _syncReactMaterialSelections(storeName, source) {
    if (typeof window === 'undefined' || !window[storeName]) {
      return;
    }

    const reactEntries = (window[storeName] || []).map(entry =>
      this._normalizeReactSelectionEntry(entry)
    );

    const allProducts = this._getAllSyncProducts();
    if (allProducts.length === 0) {
      console.warn(`[SelectionManager] No products available for syncing ${source} materials`);
      return;
    }

    const beforeKeys = new Set(
      this.selectedProducts
        .filter(item => item.source === source)
        .map(item =>
          this._getSelectionCompositeKey(
            item.productId,
            item.variant?.id,
            item.sectionId || item.instanceId
          )
        )
        .filter(Boolean)
    );

    const existingKeys = new Set(beforeKeys);

    reactEntries.forEach(({ variant, productId, sectionId, instanceId }) => {
      if (!variant) return;

      const scopeId = sectionId || instanceId;
      const compositeKey = this._getSelectionCompositeKey(
        productId,
        variant.id,
        scopeId
      );
      if (!compositeKey) {
        return;
      }

      const resolvedProductId = this.extractProductId(productId || variant.parentProductId);
      let product = resolvedProductId
        ? allProducts.find(p => String(p.id) === String(resolvedProductId))
        : null;

      if (!product && resolvedProductId) {
        product = allProducts.find(p =>
          (p.variants || []).some(v => this.extractVariantId(v.id) === this.extractVariantId(variant.id))
        );
      }

      if (!product) {
        if (existingKeys.has(compositeKey)) return;
        console.warn('[SelectionManager] Could not find product for variant:', {
          source,
          compositeKey,
          variant,
          productId: resolvedProductId,
        });
        return;
      }

      const purchaseType = variant.option2 ||
        (variant.title && variant.title.includes('Rent') ? 'Rent' : 'Buy') ||
        'Buy';

      const selectedFormat = variant.option1 === 'Digital' || variant.option1 === 'Print'
        ? variant.option1.toLowerCase()
        : 'simple';

      const cartProps = this._collectCartLineProps({
        product,
        variant,
        sectionId: sectionId || instanceId,
      });

      if (existingKeys.has(compositeKey)) {
        const existing = this.selectedProducts.find(item => {
          if (item.source !== source) return false;
          return (
            this._getSelectionCompositeKey(
              item.productId,
              item.variant?.id,
              item.sectionId || item.instanceId
            ) === compositeKey
          );
        });
        if (existing) {
          Object.assign(
            existing,
            this._collectCartLineProps({
              product,
              variant,
              sectionId: sectionId || instanceId,
              existing,
            })
          );
        }
        return;
      }

      this.selectedProducts.push({
        productId: product.id,
        productTitle: product.title || 'Unknown Product',
        selectedFormat,
        purchaseType,
        variant,
        price: parseFloat(variant.price) || 0,
        source,
        sectionId: sectionId || '',
        instanceId: instanceId || sectionId || '',
        ...cartProps,
      });

      existingKeys.add(compositeKey);
    });

    const currentKeys = new Set(
      reactEntries
        .map(({ variant, productId, sectionId, instanceId }) =>
          this._getSelectionCompositeKey(
            productId,
            variant?.id,
            sectionId || instanceId
          )
        )
        .filter(Boolean)
    );

    const liveSectionIds = new Set(
      Array.from(document.querySelectorAll('.course-card'))
        .map(card => String(card.dataset?.selectedSectionId || '').trim())
        .filter(Boolean)
    );

    this.selectedProducts = this.selectedProducts.filter(item => {
      if (item.source !== source) {
        return true;
      }
      const key = this._getSelectionCompositeKey(
        item.productId,
        item.variant?.id,
        item.sectionId || item.instanceId
      );
      if (currentKeys.has(key)) return true;
      const sid = String(item.sectionId || '').trim();
      return Boolean(sid && !liveSectionIds.has(sid));
    });

    this._dedupeSelectedProducts();

    const afterKeys = new Set(
      this.selectedProducts
        .filter(item => item.source === source)
        .map(item =>
          this._getSelectionCompositeKey(
            item.productId,
            item.variant?.id,
            item.sectionId || item.instanceId
          )
        )
        .filter(Boolean)
    );

    const changed =
      beforeKeys.size !== afterKeys.size ||
      [...beforeKeys].some(key => !afterKeys.has(key));

    if (changed) {
      this.dispatchSelectionChangeEvent();
    }
  }

  /**
   * Syncs selected variants from window.selectedSuggestedMaterials to SelectionManager
   * This bridges the React component selections with the SelectionManager
   * @returns {void}
   */
  syncSuggestedMaterials() {
    if (this._isSyncing) {
      return;
    }

    this._isSyncing = true;

    try {
      this._syncReactMaterialSelections('selectedSuggestedMaterials', 'suggested');
    } finally {
      this._isSyncing = false;
    }
  }

  /**
   * Syncs selected variants from window.selectedBundleMaterials to SelectionManager
   * @returns {void}
   */
  syncBundleMaterials() {
    if (this._isSyncing) {
      return;
    }

    this._isSyncing = true;

    try {
      this._syncReactMaterialSelections('selectedBundleMaterials', 'bundle');
    } finally {
      this._isSyncing = false;
    }
  }

  /**
   * Extract numeric variant ID from Shopify GID
   * @private
   * @param {string} gid - Shopify GID
   * @returns {string} Numeric ID
   */
  extractVariantId(gid) {
    if (!gid) return null;
    if (typeof gid !== 'string') return String(gid);
    const parts = gid.split('/');
    return parts[parts.length - 1];
  }

  /**
   * Extract numeric product ID from Shopify GID
   * @private
   * @param {string} gid - Shopify GID
   * @returns {string} Numeric ID
   */
  extractProductId(gid) {
    return this.extractVariantId(gid); // Same format
  }
}

// Export for use in other modules and global scope
window.SelectionManager = SelectionManager;

// Create namespace if it doesn't exist
if (!window.CourseMatSystem) {
  window.CourseMatSystem = {};
}
window.CourseMatSystem.SelectionManager = SelectionManager;
